import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="image-carousel"
export default class extends Controller {
  static targets = ["track"];
  static values = { images: Array };

  connect() {
    this.currentIndex = 0;
    this.slides = this.trackTarget.children;
    
    // Try to parse the additional images from the data attribute
    const imagesData = this.element.getAttribute('data-image-carousel-images');
    
    try {
      this.additionalImages = imagesData ? JSON.parse(imagesData) : [];
    } catch (e) {
      this.additionalImages = [];
    }
    
    this.totalImages = this.slides.length + this.additionalImages.length;
    this.updateSlides();
    
    // Initialize touch/swipe support
    this.initializeTouchSupport();
  }

  initializeTouchSupport() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;
    let isSwiping = false;

    // Touch start
    this.element.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isSwiping = false;
    }, { passive: true });

    // Touch move
    this.element.addEventListener('touchmove', (e) => {
      if (!startX || !startY) return;
      
      endX = e.touches[0].clientX;
      endY = e.touches[0].clientY;
      
      const diffX = startX - endX;
      const diffY = startY - endY;
      
      // Check if this is a horizontal swipe (more horizontal than vertical movement)
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        isSwiping = true;
        e.preventDefault(); // Prevent scrolling when swiping
      }
    }, { passive: false });

    // Touch end
    this.element.addEventListener('touchend', (e) => {
      if (!isSwiping || !startX || !endX) return;
      
      const diffX = startX - endX;
      const minSwipeDistance = 50; // Minimum distance for a swipe
      
      if (Math.abs(diffX) > minSwipeDistance) {
        if (diffX > 0) {
          // Swiped left - go to next
          this.next();
        } else {
          // Swiped right - go to previous
          this.prev();
        }
      }
      
      // Reset
      startX = 0;
      startY = 0;
      endX = 0;
      endY = 0;
      isSwiping = false;
    }, { passive: true });
  }

  next() {
    if (this.currentIndex < this.totalImages - 1) {
      this.currentIndex++;
      this.loadImageIfNeeded();
      // Preload 2 images ahead
      this.preloadImagesAhead(2);
      this.updateSlides();
    }
    this.updateNextButtonVisibility();
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.loadImageIfNeeded();
      this.updateSlides();
    }
    this.updatePrevButtonVisibility();
  }

  nextButtonClicked(event) {
    event.stopPropagation();
    this.next();
  }

  prevButtonClicked(event) {
    event.stopPropagation();
    this.prev();
  }

  loadImageIfNeeded() {
    // Load all slides up to the current index if needed
    while (this.currentIndex >= this.slides.length && this.additionalImages.length > 0) {
      const additionalIndex = this.slides.length - 2; // Subtract the initial 2 slides
      
      if (additionalIndex < this.additionalImages.length) {
        const imageUrl = this.additionalImages[additionalIndex];
        
        // Create a new slide element
        const newSlide = document.createElement('div');
        newSlide.className = 'carousel-slide';
        
        const link = document.createElement('a');
        link.href = this.getListingUrl();
        link.style.textDecoration = 'none';
        link.style.display = 'block';
        link.style.width = '100%';
        link.style.height = '100%';
        
        const img = document.createElement('img');
        img.src = imageUrl;
        img.onerror = () => {
          img.src = this.getNoImageUrl();
        };
        img.alt = `Listing photo ${this.slides.length + 1}`;
        
        link.appendChild(img);
        newSlide.appendChild(link);
        this.trackTarget.appendChild(newSlide);
        
        // Update slides reference
        this.slides = this.trackTarget.children;
      } else {
        break; // Prevent infinite loop
      }
    }
  }

  preloadImagesAhead(count) {
    const targetIndex = this.currentIndex + count;
    
    // Load images up to the target index
    while (this.slides.length <= targetIndex && this.additionalImages.length > 0) {
      const additionalIndex = this.slides.length - 2; // Subtract the initial 2 slides
      
      if (additionalIndex < this.additionalImages.length) {
        const imageUrl = this.additionalImages[additionalIndex];
        
        // Create a new slide element
        const newSlide = document.createElement('div');
        newSlide.className = 'carousel-slide';
        
        const link = document.createElement('a');
        link.href = this.getListingUrl();
        link.style.textDecoration = 'none';
        link.style.display = 'block';
        link.style.width = '100%';
        link.style.height = '100%';
        
        const img = document.createElement('img');
        img.src = imageUrl;
        img.onerror = () => {
          img.src = this.getNoImageUrl();
        };
        img.alt = `Listing photo ${this.slides.length + 1}`;
        
        link.appendChild(img);
        newSlide.appendChild(link);
        this.trackTarget.appendChild(newSlide);
        
        // Update slides reference
        this.slides = this.trackTarget.children;
      } else {
        break; // Prevent infinite loop
      }
    }
  }

  getListingUrl() {
    // Try to get the URL from the first slide's link
    const firstLink = this.slides[0]?.querySelector('a');
    return firstLink ? firstLink.href : '#';
  }

  getNoImageUrl() {
    // Return the no image URL
    return noImageUrl;
  }

  updateSlides() {
    // Apply transform to the track container
    this.trackTarget.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    this.updateNextButtonVisibility();
    this.updatePrevButtonVisibility();
  }

  updateNextButtonVisibility() {
    const nextButton = this.element.querySelector('.carousel-button.next');
    if (nextButton) {
      if (this.currentIndex >= this.totalImages - 1) {
        nextButton.classList.add('disabled');
      } else {
        nextButton.classList.remove('disabled');
      }
    }
  }

  updatePrevButtonVisibility() {
    const prevButton = this.element.querySelector('.carousel-button.prev');
    if (prevButton) {
      if (this.currentIndex <= 0) {
        prevButton.classList.add('disabled');
      } else {
        prevButton.classList.remove('disabled');
      }
    }
  }
}
